import { z } from "zod";

/** A team is 5 to 10 players in the product brief; the app accepts 1 to 10. */
export const MAX_TEAM_MEMBERS = 10;

/** How long an invite link works. */
export const INVITE_LIFETIME_DAYS = 7;

export const TEAM_ROLES = ["owner", "captain", "member"] as const;
export const TeamRole = z.enum(TEAM_ROLES);
export type TeamRole = z.infer<typeof TeamRole>;

/** Owners and captains manage invites and members. */
export const canManageTeam = (role: TeamRole): boolean => role === "owner" || role === "captain";

export const TeamName = z
  .string()
  .trim()
  .min(2, "At least 2 characters")
  .max(32, "At most 32 characters")
  .regex(/^[\p{L}\p{N} ._'-]+$/u, "Letters, numbers, spaces and . _ ' - only");

export const CreateTeamInput = z.object({ name: TeamName });
export type CreateTeamInput = z.infer<typeof CreateTeamInput>;
export const UpdateTeamInput = z.object({ name: TeamName });
export type UpdateTeamInput = z.infer<typeof UpdateTeamInput>;

/** Invite codes are 12 URL-safe characters. */
export const InviteCode = z.string().regex(/^[A-Za-z0-9_-]{12}$/, "Not an invite code");

export const TeamSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  role: TeamRole,
  memberCount: z.number().int(),
});
export type TeamSummary = z.infer<typeof TeamSummary>;

export const TeamMember = z.object({
  userId: z.string().uuid(),
  steamId: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  role: TeamRole,
  joinedAt: z.string(),
});
export type TeamMember = z.infer<typeof TeamMember>;

export const Invite = z.object({
  code: InviteCode,
  expiresAt: z.string(),
});
export type Invite = z.infer<typeof Invite>;

export const TeamDetail = z.object({
  id: z.string().uuid(),
  name: z.string(),
  myRole: TeamRole,
  members: z.array(TeamMember),
  /** Only owners and captains see the current invite. */
  invite: Invite.nullable(),
});
export type TeamDetail = z.infer<typeof TeamDetail>;

/** Where a player likes to play: a site or mid, and their spot there ("B short", "A long"). */
export const PREFERRED_AREAS = ["a", "b", "mid"] as const;
export const PreferredArea = z.enum(PREFERRED_AREAS);
export type PreferredArea = z.infer<typeof PreferredArea>;

/** A spot on a map, as one of its callouts: `key` is "<floor>|<callout key>" ("upper|base:Jungle#0"). */
export const PositionSpot = z.object({
  key: z.string().min(1).max(120),
  name: z.string().trim().min(1).max(30, "At most 30 characters"),
});
export type PositionSpot = z.infer<typeof PositionSpot>;
export const MAX_BACKUP_SPOTS = 2;

export const MemberPositionInput = z.object({
  map: z.string(),
  side: z.enum(["t", "ct"]),
  /** The site of the main spot (from its callout); null outside the sites. */
  area: PreferredArea.nullable(),
  /** The main spot's name. */
  spot: z.string().trim().max(30, "At most 30 characters").default(""),
  /** Which callout the main spot is; null for spots written by hand before 0.9.1. */
  spotKey: z.string().max(120).nullable().default(null),
  /** Where they also play (they rotate, or take the other site): up to two. */
  backups: z
    .array(PositionSpot)
    .max(MAX_BACKUP_SPOTS, `At most ${MAX_BACKUP_SPOTS} backup spots`)
    .default([])
    .refine((b) => new Set(b.map((x) => x.key)).size === b.length, { message: "A backup spot is there twice" }),
  /** Someone else's (owners and captains only); mine without it. */
  userId: z.string().uuid().optional(),
});
export type MemberPositionInput = z.input<typeof MemberPositionInput>;

export interface MemberPosition {
  userId: string;
  map: string;
  side: "t" | "ct";
  area: PreferredArea | null;
  spot: string;
  spotKey: string | null;
  backups: PositionSpot[];
}

/** What someone opening an invite link sees before joining. */
export const InvitePreview = z.object({
  teamName: z.string(),
  memberCount: z.number().int(),
  full: z.boolean(),
  alreadyMember: z.boolean(),
  teamId: z.string().uuid(),
});
export type InvitePreview = z.infer<typeof InvitePreview>;

/** Error body returned by the API. `code` is stable; the web app turns it into words. */
export const ApiError = z.object({
  code: z.string(),
  message: z.string(),
});
export type ApiError = z.infer<typeof ApiError>;

export const API_ERROR_CODES = [
  "unauthenticated",
  "forbidden",
  "not_found",
  "invalid_input",
  "team_full",
  "invite_expired",
  "already_member",
  "owner_must_transfer",
  "steam_failed",
  "rate_limited",
  "too_large",
  "unsupported_file",
  "limit_reached",
  "other_source",
  "not_allowed",
  "storage_failed",
  "no_change",
  "edit_conflict",
  "invalid_replay",
  "already_exists",
  "faceit_off",
  "faceit_failed",
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];
